// "Use existing crate…" (Mike 2026-11-04): which crate did the operator put a bundle's pieces in? It replaces a bare
// window.prompt. The part's open crates are buttons (GET /surplus_crates?part_id=, read when it opens; a failed read
// just leaves the typed field), and any beacon number can be typed: one from the beacon storage rack starts a new
// crate (the flashing one JobQ picked goes back) and a new label prints naming it. Resolves the beacon ("BCN-040" or
// what was typed, trimmed) or null (Cancel, the X, Escape). The PIN is asked after it, by the caller.
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ErrorBoundary from "./ErrorBoundary.jsx";
import { loadPartCrates } from "../features/surplus/surplusBundles";

/** The part's open crates as the box lists them: same kind (surplus / owed to the same order), its own crate first. */
export function crateChoices(crates, bundle) {
  const kind = (bundle && bundle.kind) || "surplus";
  const own = bundle && bundle.crate && bundle.crate.crate_id;
  return (crates || [])
    .filter((c) => c && c.status === "open" && c.beacon_id && (c.kind || "surplus") === kind
      && (kind !== "owed" || c.owed_so_id === bundle.owed_so_id))
    .sort((a, b) => (a.crate_id === own ? -1 : 0) - (b.crate_id === own ? -1 : 0) || a.crate_id - b.crate_id);
}

export function CrateBeaconBox({ bundle, onDone }) {
  const [typed, setTyped] = useState("");
  const [crates, setCrates] = useState(null);
  const inputRef = useRef(null);
  const pcs = `${bundle.on_rack_qty ?? bundle.qty} pcs ${bundle.product_id || ""}`.trim();
  const own = bundle.crate && bundle.crate.crate_id;

  useEffect(() => {
    let live = true;
    if (bundle.part_id == null) { setCrates([]); return undefined; }
    loadPartCrates(bundle.part_id)
      .then((d) => { if (live) setCrates(crateChoices(d && d.crates, bundle)); })
      .catch(() => { if (live) setCrates([]); });
    return () => { live = false; };
  }, [bundle]);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const pick = (v) => onDone(v && String(v).trim() ? String(v).trim() : null);

  return (
    <Modal show onHide={() => onDone(null)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Use existing crate</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: "1.05rem" }}>
          Which crate did you put the <b>{pcs}</b> in?
        </p>
        {crates === null && <p className="text-muted">Looking up this part's crates…</p>}
        {crates && crates.length > 0 && (
          <div data-testid="crate-choices" style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.8rem" }}>
            {crates.map((c) => (
              <Button key={c.crate_id} variant="outline-dark" size="lg" onClick={() => pick(c.beacon_id)}>
                Crate {c.beacon_id} · {c.in_crate} pcs in it{c.crate_id === own ? " · JobQ picked this one" : ""}
              </Button>
            ))}
          </div>
        )}
        <label htmlFor="crate-beacon-typed" style={{ fontWeight: 600 }}>
          {crates && crates.length ? "Another crate? Type the number on its beacon:" : "Type the number on the crate's beacon:"}
        </label>
        <input
          id="crate-beacon-typed" ref={inputRef} className="form-control form-control-lg" value={typed}
          placeholder="BCN-031 or 31" autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") pick(typed); }}
        />
        <div className="text-muted" style={{ marginTop: "0.5rem" }}>
          Took a different beacon off the beacon storage rack? Type that one: JobQ starts the crate with it, the
          flashing one goes back on the rack, and a new label prints naming your beacon.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onDone(null)}>Cancel</Button>
        <Button variant="primary" disabled={!typed.trim()} onClick={() => pick(typed)}>Next</Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Ask which crate (see the file comment). Resolves the beacon or null. */
export default function crateBeaconPrompt(bundle) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (v) => {
      resolve(v);
      setTimeout(() => { root.unmount(); host.remove(); }, 0);
    };
    root.render(<ErrorBoundary><CrateBeaconBox bundle={bundle} onDone={done} /></ErrorBoundary>);
  });
}
